import { useState, useEffect } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { API_BASE_URL } from "@/client/config"
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/client/components/ui/sidebar"
import { AppSidebar } from "@/client/components/app-sidebar"
import { Separator } from "@/client/components/ui/separator"
import { Button } from "@/client/components/ui/button"
import { Badge } from "@/client/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/client/components/ui/dropdown-menu"
import { ArrowLeftRight, Database, Trash2, Wrench } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { useScaledroneContext } from "@/client/providers/ScaledroneProvider"
import { useTriageSSE } from "@/client/hooks/useTriageSSE"
import { useAlertStore } from "@/client/store/alertStore"
import { useEncoderWSSStore } from "@/client/store/encoderWSSStore"
import { TriageIndicator } from "@/client/components/triage-indicator"
import { ConfigSheet } from "@/client/components/config-sheet"
import { showFlappingToast } from "@/client/components/flapping-toast"
import type { UserMetadata, GetFlappingResponse } from "@shared/types"

interface LayoutProps {
  children: React.ReactNode
}

const USER_METADATA_KEY = 'http://invintus_user_metadata'

export function Layout({ children }: LayoutProps) {
  const { getAccessTokenSilently, user, isAuthenticated, isLoading } = useAuth0()
  const [dumping, setDumping] = useState(false)
  const [clearing, setClearing] = useState(false)

  // Get Scaledrone status from context (managed at root level)
  const { status: wsStatus, subscriptionProgress } = useScaledroneContext()

  // Alert store for global triage state
  const {
    addAlert,
    updateAlert,
    removeAlert,
    setAlerts,
    addTriageSession,
    removeTriageSession,
    setTriageSessions,
    updateTriageSessionSample,
    setSseStatus,
  } = useAlertStore()

  // Encoder WSS store for flapping state
  const {
    setFlappingFromServer,
    handleServerFlapping,
    handleServerFlappingResolved,
  } = useEncoderWSSStore()

  // Global SSE connection for triage alerts
  const { status: sseStatus, connect: connectSSE } = useTriageSSE({
    autoConnect: false,
    onAlert: (alert) => {
      addAlert(alert)
    },
    onAlertUpdate: ({ encoderId, alertType, lastAnomalyAt, currentValue }) => {
      updateAlert(encoderId, alertType, lastAnomalyAt, currentValue)
    },
    onAlertResolved: ({ encoderId, alertType }) => {
      removeAlert(encoderId, alertType)
    },
    onTriageStarted: (data) => {
      addTriageSession(data.session)
    },
    onTriageEnded: (data) => {
      removeTriageSession(data.encoderId)
    },
    onFlapping: (alert) => {
      // Only show toast if client-side hasn't already detected this
      const existing = useEncoderWSSStore.getState().encoders.get(alert.encoderId);
      if (!existing || existing.state !== alert.severity) {
        showFlappingToast({
          encoderId: alert.encoderId,
          simpleName: alert.simpleName,
          cycles: alert.cycles,
          severity: alert.severity,
          isLiveStreamDisconnect: alert.isLiveStreamDisconnect,
        });
      }
      handleServerFlapping(alert);
    },
    onFlappingResolved: (resolved) => {
      handleServerFlappingResolved(resolved)
    },
    onEvent: (event) => {
      // Handle encoder:update for live sample data
      if (event.type === 'encoder:update') {
        const { encoderId, sample } = event.data as { encoderId: string; sample: unknown }
        if (sample) {
          updateTriageSessionSample(encoderId, sample as import('@shared/types').EncoderSampleData)
        }
      }
    },
  })

  // Update store SSE status
  useEffect(() => {
    setSseStatus(sseStatus)
  }, [sseStatus, setSseStatus])

  // Connect SSE and fetch initial triage sessions + alerts + flapping when authenticated
  useEffect(() => {
    if (isLoading || !isAuthenticated) return

    let mounted = true

    const init = async () => {
      try {
        const token = await getAccessTokenSilently()

        // Fetch initial triage sessions, active alerts, and flapping state in parallel
        const [triageResponse, alertsResponse, flappingResponse] = await Promise.all([
          fetch(`${API_BASE_URL}/api/encoders/triage`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/encoders/alerts`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/encoders/flapping`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ])

        if (!mounted) return

        if (triageResponse.ok) {
          const data = await triageResponse.json()
          setTriageSessions(data.sessions || [])
        }

        if (alertsResponse.ok) {
          const data = await alertsResponse.json()
          setAlerts(data.alerts || [])
        }

        if (flappingResponse.ok) {
          const data: GetFlappingResponse = await flappingResponse.json()
          setFlappingFromServer(data.active || [])
        }

        // Connect SSE for real-time updates
        connectSSE()
      } catch (err) {
        console.error('[Layout] Error initializing triage:', err)
      }
    }

    init()

    return () => {
      mounted = false
    }
    // Note: connectSSE now has stable identity (uses refs for callbacks)
  }, [isLoading, isAuthenticated, getAccessTokenSilently, setTriageSessions, setAlerts, setFlappingFromServer, connectSSE])

  const handleClearCache = async () => {
    setClearing(true)
    try {
      const token = await getAccessTokenSilently()
      const response = await fetch(`${API_BASE_URL}/api/cache/clear`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      const data = await response.json()
      if (data.success) {
        alert(data.message)
      } else {
        alert(`Failed to clear cache: ${data.message}`)
      }
    } catch (err) {
      console.error('Error clearing cache:', err)
      alert('Failed to clear cache')
    } finally {
      setClearing(false)
    }
  }

  const handleDumpCache = async () => {
    setDumping(true)
    try {
      const token = await getAccessTokenSilently()
      const response = await fetch(`${API_BASE_URL}/api/cache/dump`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      })
      const data = await response.json()
      if (data.success) {
        alert(`Cache dumped to: ${data.filepath}`)
      } else {
        alert(`Failed to dump cache: ${data.message}`)
      }
    } catch (err) {
      console.error('Error dumping cache:', err)
      alert('Failed to dump cache')
    } finally {
      setDumping(false)
    }
  }

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />

          {/* WebSocket Status Badge */}
          <Badge
            variant={wsStatus === 'connected' ? 'default' : 'destructive'}
            className={`dark:text-white font-mono font-normal ${
              wsStatus === 'connected' ? 'bg-green-600 hover:bg-green-700' :
              wsStatus === 'connecting' ? 'bg-yellow-600 hover:bg-yellow-700' :
              'bg-red-600 hover:bg-red-700'
            }`}
          >
            {subscriptionProgress ? (
              `Rooms: ${subscriptionProgress.subscribed}/${subscriptionProgress.total}`
            ) : (
              `WSS`
            )}
          </Badge>

          {/* Triage Indicator */}
          <TriageIndicator />

          <div className="ml-auto flex items-center gap-2">
            {process.env.NODE_ENV === 'development' && (
              <>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Wrench className="h-4 w-4 mr-2" />
                      Dev Tools
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      onClick={handleClearCache}
                      disabled={clearing}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {clearing ? 'Clearing...' : 'Clear Cache'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={handleDumpCache}
                      disabled={dumping}
                    >
                      <Database className="h-4 w-4 mr-2" />
                      {dumping ? 'Dumping...' : 'Dump Cache'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Separator orientation="vertical" className="mx-2 h-4" />
              </>
            )}

            {/* App Config reference */}
            <ConfigSheet />
            <Separator orientation="vertical" className="mx-2 h-4" />
            
            {user?.email && (
              <Link
                to="/profile"
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                {user.email}
              </Link>
            )}
            {(user?.[USER_METADATA_KEY] as UserMetadata | undefined)?.loginID && (
              <>
                <Separator orientation="vertical" className="mx-2 h-4" />
                <span className="text-sm text-muted-foreground font-mono">
                  UserID: {(user?.[USER_METADATA_KEY] as UserMetadata).loginID}
                </span>
                <ArrowLeftRight className="h-3 w-3 text-muted-foreground mx-1" />
                <Link
                  to="/utilities/swap-user-client"
                  className="text-sm text-muted-foreground font-mono hover:text-foreground transition-colors"
                >
                  {(user?.[USER_METADATA_KEY] as UserMetadata).clientID}
                </Link>
              </>
            )}
          </div>
        </header>
        <div className="flex flex-1 flex-col gap-4 p-4 min-w-0">
          <div className="flex-1 rounded-lg border bg-card p-4 min-w-0 overflow-hidden">
            {children}
          </div>
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
